(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger || !document.querySelector("#MainContent"))
    return;

  gsap.registerPlugin(ScrollTrigger);
  const sections = new Map();
  const staticStories = new WeakSet();
  let refreshFrame = 0;
  let fitFrame = 0;
  const refresh = () => {
    cancelAnimationFrame(refreshFrame);
    refreshFrame = requestAnimationFrame(() => {
      if (sections.size) ScrollTrigger.refresh(true);
    });
  };
  const editorialTargets =
    ".mx-section-head, .mx-intro > div, .mx-plan, .mx-step, .ms-principle, .ms-spec, .ms-location, .mx-closing__inner";
  const excluded = "form, [data-monarch-calculator], .mc-product, .mc-cart";

  function initialize(section) {
    if (sections.has(section) || !section.isConnected) return;
    const media = gsap.matchMedia();
    const cleanup = [];
    sections.set(section, { media, cleanup });
    try {
      media.add(
        {
          motion: "(prefers-reduced-motion: no-preference)",
          cinematic:
            "(min-width: 1024px) and (min-height: 760px) and (pointer: fine)",
        },
        (context) => {
          if (!context.conditions.motion) return;
          const desktop = context.conditions.cinematic;
          const focused = new Map();
          const story = section.querySelector("[data-motion-story]");
          const editor = document.body.dataset.motionEditor === "true";
          const restore = [];

          // Build in document order. Pin spacing must exist before later reveals.
          const hero = section.querySelector(".mx-hero__media img");
          if (desktop && hero) {
            gsap.to(hero, {
              scale: 1.07,
              yPercent: 2,
              ease: "none",
              scrollTrigger: {
                trigger: hero.parentElement,
                start: "top top",
                end: "bottom top",
                scrub: 0.65,
              },
            });
          }

          // Do not insert pin spacing above a restored scroll or deep-link target.
          const storyAhead =
            story &&
            (window.scrollY < 1 ||
              story.getBoundingClientRect().top > window.innerHeight);
          if (
            story &&
            desktop &&
            !editor &&
            !window.location.hash &&
            storyAhead &&
            !staticStories.has(story)
          ) {
            story.classList.add("mx-story--cinematic");
            restore.push(() => story.classList.remove("mx-story--cinematic"));
            const stage = story.querySelector("[data-story-stage]");
            // Larger fonts or short viewports get the complete unpinned story.
            if (stage.scrollHeight > stage.clientHeight + 1) {
              story.classList.remove("mx-story--cinematic");
            } else {
              const chapters = Array.from(
                story.querySelectorAll("[data-story-chapter]"),
              );
              const picture = story.querySelector("[data-story-image]");
              const progress = story.querySelector("[data-story-progress]");
              gsap.set(chapters.slice(1), { opacity: 0, y: 54 });
              const sequence = gsap.timeline({
                defaults: { ease: "power2.inOut" },
                scrollTrigger: {
                  trigger: story,
                  pin: stage,
                  start: () =>
                    `top ${Math.ceil(document.querySelector(".mx-header")?.getBoundingClientRect().height || 84) + 12}px`,
                  end: () => `+=${Math.round(window.innerHeight * 1.55)}`,
                  scrub: 0.45,
                  invalidateOnRefresh: true,
                  anticipatePin: 1,
                },
              });
              sequence
                .to(chapters[0], { opacity: 0, y: -42, duration: 0.4 }, 0.6)
                .to(chapters[1], { opacity: 1, y: 0, duration: 0.6 }, 0.85)
                .to(chapters[1], { opacity: 0, y: -42, duration: 0.4 }, 2)
                .to(chapters[2], { opacity: 1, y: 0, duration: 0.6 }, 2.25)
                .fromTo(
                  picture,
                  { scale: 1.19, xPercent: 2 },
                  { scale: 1, xPercent: 0, duration: 3.4, ease: "none" },
                  0,
                )
                .fromTo(
                  progress,
                  { scaleX: 0 },
                  { scaleX: 1, duration: 3.4, ease: "none" },
                  0,
                );
            }
          }

          const targets = Array.from(
            section.querySelectorAll(editorialTargets),
          ).filter(
            (target) =>
              !target.closest(excluded) &&
              !target.querySelector("form") &&
              target.getBoundingClientRect().top > window.innerHeight * 0.9,
          );
          // Transform-only entrances keep copy legible, even if a trigger is delayed.
          targets.forEach((target, index) => {
            const tween = gsap.from(target, {
              y: desktop ? 34 : 14,
              duration: desktop ? 0.8 : 0.5,
              delay:
                desktop && target.matches(".mx-plan, .mx-step")
                  ? (index % 3) * 0.08
                  : 0,
              ease: "power3.out",
              scrollTrigger: { trigger: target, start: "top 92%", once: true },
            });
            focused.set(target, tween);
          });

          if (desktop) {
            section
              .querySelectorAll(".mx-split__media img, .mx-supply-tease > img")
              .forEach((picture) => {
                gsap.fromTo(
                  picture,
                  { scale: 1.12, yPercent: -2 },
                  {
                    scale: 1.04,
                    yPercent: 2,
                    ease: "none",
                    scrollTrigger: {
                      trigger: picture.parentElement,
                      start: "top bottom",
                      end: "bottom top",
                      scrub: 0.7,
                    },
                  },
                );
              });
          }

          const revealFocus = (event) => {
            focused.forEach((tween, target) => {
              if (target.contains(event.target)) {
                tween.progress(1);
                tween.scrollTrigger?.kill();
              }
            });
          };
          section.addEventListener("focusin", revealFocus);
          return () => {
            section.removeEventListener("focusin", revealFocus);
            restore.forEach((restoreStyle) => restoreStyle());
          };
        },
      );

      section.querySelectorAll("img").forEach((picture) => {
        if (picture.complete) return;
        picture.addEventListener("load", refresh, { once: true });
        cleanup.push(() => picture.removeEventListener("load", refresh));
      });
      // Native disclosure changes alter the offsets of sections below them.
      section.querySelectorAll("details").forEach((details) => {
        details.addEventListener("toggle", refresh);
        cleanup.push(() => details.removeEventListener("toggle", refresh));
      });
    } catch (error) {
      destroy(section);
      section
        .querySelectorAll(".mx-story--cinematic")
        .forEach((story) => story.classList.remove("mx-story--cinematic"));
      console.warn(
        "MONARCH motion unavailable; showing the complete page.",
        error,
      );
    }
  }

  function destroy(section) {
    const instance = sections.get(section);
    if (!instance) return;
    instance.media.revert();
    instance.cleanup.forEach((removeListener) => removeListener());
    sections.delete(section);
  }

  function initializePage() {
    document
      .querySelectorAll("#MainContent > .shopify-section")
      .forEach(initialize);
    refresh();
  }
  // A resize can change line wrapping without crossing a media breakpoint.
  // Defer cleanup until ScrollTrigger has finished its refresh, then retain the
  // complete static story for this visit rather than clipping a pinned chapter.
  const checkStoryFit = () => {
    cancelAnimationFrame(fitFrame);
    fitFrame = requestAnimationFrame(() => {
      const overflow = Array.from(sections.keys()).flatMap((section) => {
        const story = section.querySelector(".mx-story--cinematic");
        const stage = story?.querySelector("[data-story-stage]");
        return stage && stage.scrollHeight > stage.clientHeight + 1
          ? [{ section, story }]
          : [];
      });
      overflow.forEach(({ section, story }) => {
        staticStories.add(story);
        destroy(section);
        initialize(section);
      });
      if (overflow.length) refresh();
    });
  };
  ScrollTrigger.addEventListener("refresh", checkStoryFit);
  // Rendering never waits for this; only animation geometry waits for font metrics.
  if (document.fonts?.ready) document.fonts.ready.then(initializePage);
  else initializePage();
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.closest("#MainContent")) {
      initialize(event.target);
      refresh();
    }
  });
  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
    refresh();
  });
  document.addEventListener("shopify:section:reorder", refresh);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) refresh();
  });
})();
